import type { ReactNode } from "react";
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import {
  DATE_BASIS_SORT_KEY,
  dateBasisForSortKey,
  useStoredDocumentListDateBasis,
} from "../hooks/useDocumentListDateBasis";
import type { SortKey, SortOrder } from "../utils/documentSorting";
import type { UploadDateRange } from "@/stores/documents/repository";

/** The Added filter: when documents reached Franklin, and how it was chosen. */
export interface AddedDateFilterValue {
  preset: "7d" | "30d" | "custom";
  range: UploadDateRange;
}

export interface DocumentFiltersState {
  sortKey: SortKey;
  sortOrder: SortOrder;
  searchQuery: string;
  activeTagFilters: string[];
  /** null = any time. */
  addedDate: AddedDateFilterValue | null;
}

export interface DocumentFiltersContextType {
  /**
   * `sortKey` here is the EFFECTIVE key: a date sort (either date key) reads as
   * the date the list's one date column shows (`useDocumentListDateBasis`), so
   * a list never orders by a date it is not showing. The stored key itself is
   * never rewritten by that preference.
   */
  filters: DocumentFiltersState;
  /**
   * True until the viewer picks a sort: the folders view keeps its tiered and
   * folders-first layouts only then. Tracked as its own flag, not read off the
   * key: the default (`created_at` desc) is also what the Uploaded column's
   * arrow sorts by, so comparing values cannot tell "never sorted" from "sorted
   * by date, newest first". A "File modified" column choice is not a sort.
   */
  sortIsDefault: boolean;
  /**
   * The `searchQuery` the mounted view has finished resolving into rows, or
   * `null` when it has resolved nothing yet (fresh mount, or a tab switch that
   * mounted a different view). The search box is busy exactly while this
   * differs from `filters.searchQuery`.
   *
   * A query rather than an `isSearching` boolean, because a boolean cannot say
   * WHICH query the rows on screen belong to: it goes false for one commit at
   * dispatch (before the view's effect has run), and it cannot distinguish a
   * fetch this search caused from one that pagination, a rename refetch or a
   * realtime resync caused. Exactly one document view is mounted at a time, so
   * this has a single writer.
   */
  resolvedSearchQuery: string | null;
  setSortKey: (key: SortKey) => void;
  setSortOrder: (order: SortOrder) => void;
  setSearchQuery: (query: string) => void;
  setActiveTagFilters: (filters: string[]) => void;
  setAddedDate: (value: AddedDateFilterValue | null) => void;
  /** Drops every filter that narrows the rows — tags, search, Added — and keeps the sort. */
  clearFilters: () => void;
  reportResolvedSearchQuery: (query: string | null) => void;
}

const defaultFilters: DocumentFiltersState = {
  sortKey: "created_at",
  sortOrder: "desc",
  searchQuery: "",
  activeTagFilters: [],
  addedDate: null,
};

type StoredFilters = DocumentFiltersState & { sortChosen: boolean };
const initialStored: StoredFilters = { ...defaultFilters, sortChosen: false };

const DocumentFiltersContext = createContext<DocumentFiltersContextType | undefined>(undefined);

const sameTagFilters = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, i) => value === b[i]);

export function DocumentFiltersProvider({ children }: { children: ReactNode }) {
  // `sortChosen` rides in the same state as the sort, so a setter given an
  // unchanged value still hands back the same object (see below).
  const [stored, setFilters] = useState<StoredFilters>(initialStored);
  const { sortChosen } = stored;

  // A date sort orders by the date the column shows. Derived, never written
  // back: the stored key stays what the viewer chose (or the default).
  const dateBasis = useStoredDocumentListDateBasis();
  const filters = useMemo<DocumentFiltersState>(() => {
    const { sortChosen: _chosen, ...current } = stored;
    if (dateBasisForSortKey(current.sortKey) === null) return current;
    return { ...current, sortKey: DATE_BASIS_SORT_KEY[dateBasis] };
  }, [stored, dateBasis]);
  const sortIsDefault = !sortChosen;
  const [resolvedSearchQuery, setResolvedSearchQuery] = useState<string | null>(null);

  // Every setter is stable and every one bails on an unchanged value, so the
  // context value only changes when the state actually does.
  //
  // Both halves are load-bearing. Consumers debounce and report through effects
  // keyed on these setters, so a fresh identity each render re-arms those timers
  // on every render — and a setter that hands back a new `filters` object for an
  // unchanged value re-renders this whole subtree, which re-arms them again. Left
  // together those two made a permanent ~300ms commit → render → re-arm → commit
  // loop that ran from page load with an empty search box, and starved the
  // search box's own debounce for as long as the user kept typing.
  const setSortKey = useCallback((key: SortKey): void => {
    setFilters((prev) =>
      prev.sortKey === key ? prev : { ...prev, sortKey: key, sortChosen: true }
    );
  }, []);

  const setSortOrder = useCallback((order: SortOrder): void => {
    setFilters((prev) =>
      prev.sortOrder === order ? prev : { ...prev, sortOrder: order, sortChosen: true }
    );
  }, []);

  const setSearchQuery = useCallback((query: string): void => {
    setFilters((prev) => (prev.searchQuery === query ? prev : { ...prev, searchQuery: query }));
  }, []);

  const setActiveTagFilters = useCallback((activeTagFilters: string[]): void => {
    setFilters((prev) =>
      sameTagFilters(prev.activeTagFilters, activeTagFilters) ? prev : { ...prev, activeTagFilters }
    );
  }, []);

  const setAddedDate = useCallback((addedDate: AddedDateFilterValue | null): void => {
    setFilters((prev) =>
      prev.addedDate?.preset === addedDate?.preset &&
      prev.addedDate?.range.from === addedDate?.range.from &&
      prev.addedDate?.range.to === addedDate?.range.to
        ? prev
        : { ...prev, addedDate }
    );
  }, []);

  const clearFilters = useCallback((): void => {
    setFilters((prev) =>
      prev.searchQuery === "" && prev.activeTagFilters.length === 0 && prev.addedDate === null
        ? prev
        : { ...prev, searchQuery: "", activeTagFilters: [], addedDate: null }
    );
  }, []);

  const reportResolvedSearchQuery = useCallback((query: string | null): void => {
    setResolvedSearchQuery((prev) => (prev === query ? prev : query));
  }, []);

  const contextValue = useMemo<DocumentFiltersContextType>(
    () => ({
      filters,
      sortIsDefault,
      resolvedSearchQuery,
      setSortKey,
      setSortOrder,
      setSearchQuery,
      setActiveTagFilters,
      setAddedDate,
      clearFilters,
      reportResolvedSearchQuery,
    }),
    [
      filters,
      sortIsDefault,
      resolvedSearchQuery,
      setSortKey,
      setSortOrder,
      setSearchQuery,
      setActiveTagFilters,
      setAddedDate,
      clearFilters,
      reportResolvedSearchQuery,
    ]
  );

  return (
    <DocumentFiltersContext.Provider value={contextValue}>
      {children}
    </DocumentFiltersContext.Provider>
  );
}

/**
 * Hides an ancestor provider from a subtree, so `useDocumentFiltersOptional`
 * inside it returns null.
 *
 * For a document list rendered *within* another one — the container dialog's
 * nested DocumentListTable — whose sort headers and search would otherwise
 * write the host page's filters and silently refetch the list behind it.
 */
export function DocumentFiltersReset({ children }: { children: ReactNode }) {
  return (
    <DocumentFiltersContext.Provider value={undefined}>{children}</DocumentFiltersContext.Provider>
  );
}

export function useDocumentFilters(): DocumentFiltersContextType {
  const context = useContext(DocumentFiltersContext);
  if (context === undefined) {
    throw new Error("useDocumentFilters must be used within a DocumentFiltersProvider");
  }
  return context;
}

/**
 * Optional version of useDocumentFilters that returns null when no provider exists.
 * Use in components that may render outside DocumentFiltersProvider (e.g. projects page, share page).
 */
export function useDocumentFiltersOptional(): DocumentFiltersContextType | null {
  return useContext(DocumentFiltersContext) ?? null;
}
